import { A } from '@adornis/base/env-info.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { IcsrmUniEnum } from '@adornis/bh-plugin-impl/stammdaten/icsrm-stammdaten-plugin.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { FileInput } from '@adornis/file-utils/client/file-input.js';
import { TextareaInput } from '@adornis/formfield/fields/textarea-input.js';
import { stateful, useMemo, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { useGeneratedForm } from '@adornis/generated-form/use-generated-form.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { html, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { of } from 'rxjs';
import * as m from '../../../../localization/out/messages.js';
import { withFormAutofiller } from '../../components/form-autofiller.js';
import { extractCvInformation } from '../../../api/extractCvInformation.js';
import { subscribeUserDataByUserID, updateMyName } from '../../../api/user.js';
import {
  EmploymentType,
  ExpectedAcademicTitle,
  PreferredStudyField,
  SELECTABLE_SPEECH_LEVELS,
  SpeechLevel,
} from '../../../db/job-position.js';
import { ResumeFile } from '../../../db/ResumeFile.js';
import { MWUserData, OtherSpeechLevels, PracticalExperience, PracticalExperienceLevel } from '../../../db/user.js';
import { CtaPillButton } from '../../components/cta-pill-button.js';
import { enumRenderer as makeEnumRenderer } from '../../components/enum-renderer.js';
import { entityValidator, translateValidationError } from '../../components/form-validation.js';
import { formatFormError, useScrollToError } from '../../components/format-form-error.js';
import { labeledField } from '../../components/labeled-field.js';
import '../../components/mw-country-picker.js';
import { RegisterMultiselectCheckboxes } from './register-multiselect-checkboxes.js';

// Custom renderers for the new demographic fields. Inline here because they
// aren't reused elsewhere — extract if a second consumer appears.
const countryPickerRenderer = (props: any) => html`
  <mw-country-picker
    .value=${props.value ?? null}
    .error=${props.error ?? ''}
    ?disabled=${props.disabled}
    @value-picked=${(e: CustomEvent) => props.onPicked?.(e.detail.value ?? null)}
  ></mw-country-picker>
`;

const yesNoRenderer = (props: any) => html`
  <select
    class="select select-bordered w-full ${props.error ? 'select-error' : ''}"
    ?disabled=${props.disabled}
    @change=${(e: Event) => {
      const raw = (e.target as HTMLSelectElement).value;
      const value = raw === '' ? null : raw === 'true';
      props.onPicked?.(value);
    }}
  >
    <option value="" ?selected=${props.value === null || props.value === undefined}>
      ${m.register_onboarding_personal_info_yesno_placeholder()}
    </option>
    <option value="true" ?selected=${props.value === true}>${m.yes()}</option>
    <option value="false" ?selected=${props.value === false}>${m.no()}</option>
  </select>
  ${props.error ? html`<div class="text-error text-xs mt-1">${props.error}</div>` : nothing}
`;

/** Bound to this form's translation prefix; matches `cv_extract_form_enum_<VALUE>` keys. */
const enumRenderer = <E extends Record<string, string>>(enumType: E, multi = false) =>
  makeEnumRenderer(enumType, 'cv_extract_form_enum', multi);

@Entity()
export class PDFUpload extends AdornisEntity {
  static override _class = 'PDFUpload';

  // Dedicated translation keys instead of the generic `validation_non_optional`
  // (`Wert "{key}" ist erforderlich!`): this form has no `pdf_upload_form_consent_label`
  // to interpolate, and a consent checkbox reads better with its own sentence anyway.
  @validate(nonOptional({ translationKey: 'validation_cv_file_required' }))
  @Field(type => ID)
  fileID!: string;

  // nonOptional() rejects every falsy value, so an unchecked box (`false`) fails
  // just like an untouched one (`undefined`) — which is what we want here.
  @validate(nonOptional({ translationKey: 'validation_cv_consent_required' }))
  @Field(type => Boolean)
  consent!: boolean;
}

// PDFUpload is a throwaway client-only entity that is never saved, so this form's
// validator is the only thing standing between the user and an unconfirmed consent.
const PDF_UPLOAD_VALIDATOR = entityValidator(PDFUpload);
// Every @validate'd MWUserData field is rendered on this step, so the full set applies.
const CV_EXTRACT_VALIDATOR = entityValidator(MWUserData);
// The name form is a two-field view of User, whose decorators also mark `suspentions`
// as required. It is not queried or rendered here, so narrow the validator.
const USER_NAME_VALIDATOR = entityValidator(User, ['firstName', 'lastName']);

export const RegisterOnboardingPersonalInfo = stateful(({ onFinished }: { onFinished: () => void }) => {
  const user = useObservable(() => currentUser<User>(), []);

  const userDataQuery = useQuery(() => {
    if (!user?._id) return of(null);
    return subscribeUserDataByUserID(user._id)(selectionSet(() => MWUserData, 2));
  }, [user?._id]);

  // Local UI state for resume processing
  const [isProcessingCv, setIsProcessingCv] = useState(false);
  const [cvError, setCvError] = useState<string | null>(null);
  const [cvSuccess, setCvSuccess] = useState(false);
  // Local UI state for profile (name + extract) saving
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  // State for toggling between PDF upload and manual form
  const [activeOption, setActiveOption] = useState<'pdf' | 'manual' | null>(null);
  // Two banners (CV upload vs. manual profile) live in mutually-exclusive
  // sections of the template — scope each scroll to its own anchor so the
  // hook can't ever land on the wrong banner if that layout assumption
  // changes.
  useScrollToError(cvError, 'cv');
  useScrollToError(profileError, 'profile');

  const pdfUpload = useMemo(() => [new PDFUpload({})], []);
  const { form, renderField } = useGeneratedForm(
    pdfUpload,
    {
      translationPrefix: 'pdf_upload_form_',
      validator: PDF_UPLOAD_VALIDATOR,
      translateError: translateValidationError,
    },
    [],
  );

  const handlePdfUpload = async () => {
    setCvError(null);
    setCvSuccess(false);
    setIsProcessingCv(true);
    try {
      const err = await form.validate();
      if (err) {
        setCvError(formatFormError(err, 'pdf_upload_form_'));
        setIsProcessingCv(false);
        return;
      }
      await form.uploadAllPendingFiles();
      const fileID = pdfUpload.fileID;
      if (!fileID) {
        setCvError(m.register_onboarding_personal_info_error_processing());
        setIsProcessingCv(false);
        return;
      }
      await extractCvInformation(fileID)();
      setCvSuccess(true);
      // switch to manual mode for the user to double check
      setTimeout(() => {
        setActiveOption('manual');
        setIsProcessingCv(false);
      }, 1000);
    } catch (e: any) {
      console.error(e);
      setCvError(formatFormError(e, 'pdf_upload_form_'));
      setIsProcessingCv(false);
    }
  };

  const [userPlaceholder] = useState(new User({}));
  const userOrPlaceholder = user ?? userPlaceholder;
  const { form: userNameForm, renderField: renderUserNameField } = useGeneratedForm(
    userOrPlaceholder,
    {
      translationPrefix: 'user_name_form_',
      validator: USER_NAME_VALIDATOR,
      translateError: translateValidationError,
    },
    [userOrPlaceholder],
  );

  const [userDataPlaceholder] = useState(new MWUserData({}));
  const userData = userDataQuery.value ?? userDataPlaceholder;

  // Mutating array fields on `userData` (otherLanguages, practicalExperiences)
  // doesn't notify lit to re-render. Bump this after each push/splice so the
  // repeat() reads the new length on the next render.
  const [listRev, setListRev] = useState(0);
  const bumpListRev = () => setListRev(listRev + 1);

  const {
    form: cvExtractForm,
    renderField: renderCVExtractField,
    autofiller,
  } = withFormAutofiller(
    useGeneratedForm<MWUserData>(
      userData,
      {
        translationPrefix: 'cv_extract_form_',
        validator: CV_EXTRACT_VALIDATOR,
        translateError: translateValidationError,
      },
      // Must be a stable list — useGeneratedForm internally memoizes the form by
      // [...deps]. A `Math.random()` (or any value that changes per render) makes
      // the form rebuild on every render, wiping picked values before validate()
      // sees them, which surfaces as bogus "<field> is required" errors.
      [userData],
    ),
  );

  const handleProfileSave = async () => {
    setProfileError(null);
    setIsSavingProfile(true);
    try {
      const cvErr = await cvExtractForm.validate();
      if (cvErr) {
        setProfileError(formatFormError(cvErr, 'cv_extract_form_'));
        return;
      }
      // USER_NAME_VALIDATOR is already narrowed to the two fields this form renders,
      // so there is nothing untouched left that could surface a spurious error — the
      // old `validateOmitted: false` workaround (and its @ts-expect-error) is obsolete.
      const userErr = await userNameForm.validate();
      if (userErr) {
        setProfileError(formatFormError(userErr, 'user_name_form_'));
        return;
      }
      // Persist first/last name via a dedicated server mutation. We can't
      // just call `.save()` on the currentUser observable's value because
      // it isn't an AdornisEntity instance. Always send — useGeneratedForm
      // mutates the User reference in place as the user types, so any
      // "did it change?" diff against `user.firstName` is always false by
      // the time we reach this line.
      if (user) {
        const edited = userNameForm.document;
        await updateMyName((edited.firstName ?? '').trim(), (edited.lastName ?? '').trim())();
      }
      await userData.save();
      onFinished();
    } catch (e: any) {
      console.error(e);
      setProfileError(formatFormError(e, 'cv_extract_form_'));
    } finally {
      setIsSavingProfile(false);
    }
  };

  return html`
    ${autofiller}
    <div class="flex flex-col gap-6">
      ${
        activeOption === null
          ? html`
              <h2 class="text-xl font-bold">${m.how_to_create_profile()}</h2>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <!-- Option 1: PDF Upload -->
                <div
                  class="card bg-base-100 border-2 border-base-300 hover:border-primary transition-colors cursor-pointer"
                  @click=${() => setActiveOption('pdf')}
                >
                  <div class="card-body">
                    <h3 class="card-title flex items-center gap-2">
                      ${m.register_onboarding_personal_info_upload_title()}
                    </h3>
                    <p class="opacity-80 text-sm">${m.register_onboarding_personal_info_upload_desc()}</p>

                    <div class="flex flex-row justify-end mt-4">
                      <button
                        class="btn btn-outline"
                        @click=${(e: Event) => {
                          e.stopPropagation();
                          setActiveOption('pdf');
                        }}
                      >
                        ${m.select()}
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Option 2: Manual Entry -->
                <div
                  class="card bg-base-100 border-2 border-base-300 hover:border-primary transition-colors cursor-pointer"
                  @click=${() => setActiveOption('manual')}
                >
                  <div class="card-body">
                    <h3 class="card-title flex items-center gap-2">
                      ${m.register_onboarding_personal_info_fill_form_title_short()}
                    </h3>
                    <p class="opacity-80 text-sm">${m.register_onboarding_personal_info_fill_form()}</p>

                    <div class="flex flex-row justify-end mt-4">
                      <button
                        class="btn btn-outline"
                        @click=${(e: Event) => {
                          e.stopPropagation();
                          setActiveOption('manual');
                        }}
                      >
                        ${m.select()}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            `
          : html`
              <!-- Condensed option selector after choice is made -->
              <div class="flex flex-col gap-2">
                <h2 class="text-lg font-medium">${m.profile_creation()}</h2>
                <div class="flex flex-row gap-2 items-center">
                  <button
                    class="btn btn-sm ${activeOption === 'pdf' ? 'btn-primary' : 'btn-outline'} flex items-center gap-1"
                    @click=${() => setActiveOption('pdf')}
                  >
                    ${m.register_onboarding_personal_info_upload_title_short()}
                  </button>
                  <button
                    class="btn btn-sm ${
                      activeOption === 'manual' ? 'btn-primary' : 'btn-outline'
                    } flex items-center gap-1"
                    @click=${() => setActiveOption('manual')}
                  >
                    ${m.register_onboarding_personal_info_fill_form_title_short()}
                  </button>
                </div>
              </div>
            `
      }
    </div>

    ${
      activeOption === 'pdf'
        ? html`
            <div class="mt-4 p-6 border-2 border-primary rounded-lg bg-base-100">
              ${
                cvError
                  ? html`<div class="alert alert-error my-3" data-error-anchor="cv">
                      <span class="icon-[material-symbols--error] w-6 h-6"></span>
                      <span>${cvError}</span>
                    </div>`
                  : nothing
              }
              ${
                cvSuccess
                  ? html`<div class="alert alert-success my-3">
                      <span class="icon-[material-symbols--check-circle] w-6 h-6"></span>
                      <span>${m.register_onboarding_personal_info_success()}</span>
                    </div>`
                  : html`
                      ${
                        isProcessingCv
                          ? html`<div class="alert alert-info my-3">
                              <span class="loading loading-spinner"></span>
                              <span>${m.register_onboarding_personal_info_processing()}</span>
                            </div>`
                          : nothing
                      }
                    `
              }

              <div class="mt-4 space-y-3">
                ${labeledField(
                  m.pdf_upload_form_fileID_label(),
                  renderField(['fileID'], props =>
                    FileInput({
                      ...props,
                      accept: 'application/pdf,.pdf',
                      fileEntity: ResumeFile,
                      placeholder: m.cv_check_upload_label(),
                    }),
                  ),
                  { required: true },
                )}
                ${renderField(
                  ['consent'],
                  props => html`
                    <div class="flex flex-col gap-2">
                      <label class="flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          class="checkbox checkbox-success mt-1"
                          ?disabled=${props.disabled}
                          .checked=${props.value}
                          @change=${(e: Event) =>
                            props.onPicked?.((e.target as HTMLInputElement).checked)}
                        />
                        <span class="text-sm ${props.error ? 'text-error' : ''}">
                          ${m.register_onboarding_personal_info_consent_text()}
                          <a class="link link-primary" href="/privacy" target="_blank" rel="noreferrer"
                            >${m.privacy_policy()}</a
                          >
                          ${m.register_onboarding_personal_info_consent_text_after_link()}
                          <span class="text-error"> *</span>
                        </span>
                      </label>
                      ${props.error ? html`<div class="text-error text-sm">${props.error}</div>` : nothing}
                    </div>
                  `,
                )}

                <div class="text-xs opacity-70 mt-2">${m.required_fields_note()}</div>
                <div class="flex flex-row justify-end">
                  <button class="btn btn-primary" ?disabled=${isProcessingCv} @click=${handlePdfUpload}>
                    ${isProcessingCv ? html`<span class="loading loading-spinner"></span>` : nothing}
                    ${m.register_onboarding_personal_info_process_button()}
                  </button>
                </div>
              </div>
            </div>
          `
        : html``
    }
    ${
      activeOption === 'manual'
        ? html`
            <div class="divider mt-4 mb-4">${m.register_onboarding_personal_info_fill_form_title_short()}</div>

            <div class="grid grid-cols-1 gap-6">
              <div class="space-y-6">
                <div>
                  <h3 class="card-title">${m.register_onboarding_personal_info_personal_info_title()}</h3>
                  ${
                    profileError
                      ? html`<div class="alert alert-error my-3" data-error-anchor="profile">
                          <span class="icon-[material-symbols--error] w-6 h-6"></span>
                          <span>${profileError}</span>
                        </div>`
                      : nothing
                  }
                  ${
                    isSavingProfile
                      ? html`<div class="alert alert-info my-3">
                          <span class="loading loading-spinner"></span>
                          <span>${m.register_onboarding_personal_info_processing()}</span>
                        </div>`
                      : nothing
                  }
                  <div>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                      ${labeledField(m.user_name_form_firstName_label(), renderUserNameField(['firstName']), {
                        required: true,
                      })}
                      ${labeledField(m.user_name_form_lastName_label(), renderUserNameField(['lastName']), {
                        required: true,
                      })}
                      ${labeledField(m.cv_extract_form_dateOfBirth_label(), renderCVExtractField(['dateOfBirth']), {
                        required: true,
                      })}
                      ${labeledField(
                        m.cv_extract_form_nationality_label(),
                        renderCVExtractField(['nationality'], countryPickerRenderer),
                        { required: true },
                      )}
                      ${labeledField(
                        m.cv_extract_form_countryOfBirth_label(),
                        renderCVExtractField(['countryOfBirth'], countryPickerRenderer),
                        { required: true },
                      )}
                      ${labeledField(
                        m.cv_extract_form_hasMigrationBackground_label(),
                        renderCVExtractField(['hasMigrationBackground'], yesNoRenderer),
                        { required: true },
                      )}
                      ${labeledField(
                        m.cv_extract_form_university_label(),
                        renderCVExtractField(['university'], enumRenderer(IcsrmUniEnum)),
                        { required: true },
                      )}
                      ${labeledField(m.cv_extract_form_studyField_label(), renderCVExtractField(['studyField']), {
                        required: true,
                      })}
                      ${labeledField(
                        m.cv_extract_form_department_label(),
                        renderCVExtractField(['department'], enumRenderer(PreferredStudyField)),
                        { required: true },
                      )}
                      ${labeledField(
                        m.cv_extract_form_academicTitle_label(),
                        renderCVExtractField(['academicTitle'], enumRenderer(ExpectedAcademicTitle)),
                        { required: true },
                      )}
                      ${labeledField(
                        m.cv_extract_form_expectedCompletionDate_label(),
                        renderCVExtractField(['expectedCompletionDate']),
                        { required: true },
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <div class="font-medium">${m.register_onboarding_personal_info_languages_title()}</div>
                  <div class="flex flex-wrap gap-3 mt-2">
                    ${labeledField(
                      m.cv_extract_form_languageSkillsDE_label(),
                      renderCVExtractField(['languageSkillsDE'], enumRenderer(SELECTABLE_SPEECH_LEVELS)),
                      { required: true },
                    )}
                    ${labeledField(
                      m.cv_extract_form_languageSkillsEN_label(),
                      renderCVExtractField(['languageSkillsEN'], enumRenderer(SELECTABLE_SPEECH_LEVELS)),
                      { required: true },
                    )}
                    ${repeat(
                      userDataQuery.value?.otherLanguages ?? [],
                      (lang, i) => html`
                        <div class="flex flex-wrap gap-2 w-full">
                          ${labeledField(
                            m.cv_extract_form_otherLanguages_language_label(),
                            renderCVExtractField(['otherLanguages', i, 'language']),
                          )}
                          <div class="flex items-end gap-2">
                            ${labeledField(
                              m.cv_extract_form_otherLanguages_level_label(),
                              renderCVExtractField(['otherLanguages', i, 'level'], props =>
                                enumRenderer(SELECTABLE_SPEECH_LEVELS)({ ...(props as any), label: undefined }),
                              ),
                            )}
                            <button
                              class="btn btn-ghost btn-square h-[44px] w-[44px] min-h-0 p-0 flex items-center justify-center"
                              title="Entfernen"
                              aria-label="Entfernen"
                              @click=${() => {
                                if (!userDataQuery.value) return;
                                if (!userDataQuery.value.otherLanguages) return;
                                userDataQuery.value.otherLanguages.splice(i, 1);
                                bumpListRev();
                              }}
                            >
                              <span class="icon-[material-symbols--delete] w-5 h-5"></span>
                            </button>
                          </div>
                        </div>
                      `,
                    )}
                    <button
                      class="btn btn-secondary"
                      @click=${() => {
                        if (!userDataQuery.value) return;
                        userDataQuery.value.otherLanguages = [
                          ...(userDataQuery.value.otherLanguages ?? []),
                          new OtherSpeechLevels({ _id: A.getGloballyUniqueID(), language: '', level: SpeechLevel.C2 }),
                        ];
                        bumpListRev();
                      }}
                    >
                      <span class="icon-[material-symbols--add] w-5 h-5"></span>
                      ${m.register_onboarding_personal_info_add_language_button()}
                    </button>
                  </div>
                </div>
                <div>
                  <div class="font-medium">
                    ${m.register_onboarding_personal_info_it_skills_title()}<span class="text-error"> *</span>
                  </div>
                  <div class="text-xs opacity-60 mt-1">${m.register_onboarding_personal_info_it_skills_hint()}</div>
                  ${renderCVExtractField(['techSkills'], TextareaInput)}
                </div>
                <div>
                  <div class="font-medium">
                    ${m.register_onboarding_personal_info_soft_skills_title({ max: 5 })}<span class="text-error">
                      *</span
                    >
                  </div>
                  ${renderCVExtractField(['softSkills'], RegisterMultiselectCheckboxes({ maxItems: 5 }))}
                </div>
                <div>
                  <div class="font-medium">${m.register_onboarding_personal_info_practical_experience_title()}</div>
                  <div class="text-xs opacity-60 mt-1 mb-2 max-w-prose">
                    ${unsafeHTML(m.practical_experience_instructions())}
                  </div>
                  <div class="grid grid-cols-1 gap-3 mt-2">
                    ${repeat(
                      userDataQuery.value?.practicalExperiences ?? [],
                      (exp, i) => html`
                        <div
                          class="grid grid-cols-[1fr_auto] items-start gap-2 w-full border border-base-300 rounded-lg p-3"
                        >
                          <div class="grid grid-cols-1 gap-3 w-full">
                            ${labeledField(
                              m.cv_extract_form_practicalExperiences_level_label(),
                              renderCVExtractField(
                                ['practicalExperiences', i, 'level'],
                                enumRenderer(PracticalExperienceLevel),
                              ),
                            )}
                            ${labeledField(
                              m.cv_extract_form_practicalExperiences_description_label(),
                              renderCVExtractField(['practicalExperiences', i, 'description'], TextareaInput),
                            )}
                          </div>
                          <button
                            class="btn btn-ghost btn-square h-[44px] w-[44px] min-h-0 p-0 flex items-center justify-center"
                            title="Entfernen"
                            aria-label="Entfernen"
                            @click=${() => {
                              if (!userDataQuery.value) return;
                              if (!userDataQuery.value.practicalExperiences) return;
                              userDataQuery.value.practicalExperiences.splice(i, 1);
                              bumpListRev();
                            }}
                          >
                            <span class="icon-[material-symbols--delete] w-5 h-5"></span>
                          </button>
                        </div>
                      `,
                    )}
                  </div>
                  <button
                    class="btn btn-secondary mt-2"
                    @click=${() => {
                      if (!userDataQuery.value) return;
                      userDataQuery.value.practicalExperiences = [
                        ...(userDataQuery.value.practicalExperiences ?? []),
                        new PracticalExperience({
                          _id: A.getGloballyUniqueID(),
                          description: '',
                          level: PracticalExperienceLevel.NONE,
                        }),
                      ];
                      bumpListRev();
                    }}
                  >
                    <span class="icon-[material-symbols--add] w-5 h-5"></span>
                    ${m.register_onboarding_personal_info_add_practical_experience_button()}
                  </button>
                </div>
                <div>
                  <div class="font-medium">${m.register_onboarding_personal_info_desired_positions_title()}</div>
                  <div class="text-xs opacity-60 mt-1">
                    ${m.register_onboarding_personal_info_desired_positions_hint()}
                  </div>
                  ${renderCVExtractField(
                    ['desiredEmploymentTypes'],
                    RegisterMultiselectCheckboxes({ enumObject: EmploymentType }),
                  )}
                </div>
              </div>
              <div class="text-xs opacity-70 mt-2">${m.required_fields_note()}</div>
              <div class="mt-6 flex justify-end">
                ${CtaPillButton({
                  label: m.register_onboarding_personal_info_save_and_next_button(),
                  disabled: isSavingProfile,
                  onClick: handleProfileSave,
                })}
              </div>
            </div>
          `
        : html``
    }
  `;
});
